import React, { useEffect, useMemo } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { LoadingButton } from '@mui/lab';
import { TabContext, TabPanel } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Card,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  useDeleteCommunityGroup,
  useGetCommunityGroup,
  useUpdateCommunityGroup,
} from '@juno/client-api';
import {
  FeatureConfig,
  FeatureConfigConfig,
  FeatureConfigTypeEnum,
  RoleEnum,
  Site as SiteModel,
} from '@juno/client-api/model';
import { AdminEditPanelHeader, Container, DialogAriaWrapper, JunoDialog } from '@juno/ui';
import { MutationAction, a11yProps, onMutation, useSettings } from '@juno/utils';
import { ContentRouteTypeEnum, getContentRoute } from '@juno/utils';
import GroupFilesTab from '../../shared/GroupFilesTab';
import ForumFeedSettings from './ForumFeedSettings';
import GroupInfo from './GroupInfo';
import GroupMembers from './GroupMembers';
import validationSchema from './ValidationSchema';

const MODEL = 'Group';
interface CommunityGroupPanelProps {
  site: SiteModel;
}

export enum GroupAdminTabsEnum {
  INFO = 'info',
  MEMBERS = 'members',
  RESOURCES = 'resources',
  SETTINGS = 'settings',
}

export interface GroupHierarchyFeatureConfigModel extends FeatureConfig {
  config: GroupHierarchyConfigModel;
}
export interface GroupHierarchyConfigModel extends FeatureConfigConfig {
  group_hierarchy?: boolean;
}

const headerSkeletons = (
  <Grid container spacing={2} mt={4}>
    <Grid item xs={1}>
      <Skeleton variant='circular' height={50} width={50} sx={{ mt: 2 }} />
    </Grid>
    <Grid item xs={6}>
      <Skeleton variant='rectangular' height={20} sx={{ mb: 1 }} width={80} />
      <Skeleton variant='rectangular' height={50} />
    </Grid>
  </Grid>
);

const skeletons = (
  <Container>
    <Grid container spacing={2} mt={4}>
      <Grid item xs={12}>
        <Skeleton variant='rectangular' height={60} />
      </Grid>
      <Grid item xs={9}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Skeleton variant='rectangular' height={40} />
          </Grid>
          <Grid item xs={6}>
            <Skeleton variant='rectangular' height={40} />
          </Grid>
          <Grid item xs={12}>
            <Skeleton variant='rectangular' height={40} />
          </Grid>
          <Grid item xs={12}>
            <Skeleton variant='rectangular' height={400} />
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={3}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Skeleton variant='rectangular' height={60} />
          </Grid>
          <Grid item xs={12}>
            <Skeleton variant='rectangular' height={60} />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  </Container>
);

const CommunityGroupPanel: React.FC<CommunityGroupPanelProps> = ({ site }) => {
  const { id: siteId } = site;
  const { groupSlug } = useParams();
  const { pathname } = useLocation();
  const { configs, user, impliedAdminOfGroups } = useSettings();
  const { tabSlug } = useParams();
  const [tab, setTab] = React.useState(tabSlug || GroupAdminTabsEnum.INFO);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const navigate = useNavigate();
  const { data, isLoading, refetch } = useGetCommunityGroup(siteId, groupSlug || '');
  const groupUpdate = useUpdateCommunityGroup(onMutation(MutationAction.UPDATE, MODEL, refetch));
  const deleteGroup = useDeleteCommunityGroup(onMutation(MutationAction.DELETE, MODEL, () => {}));
  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTab(newValue);
  };

  const iAmAdmin = useMemo(() => {
    if (user?.role === RoleEnum.NUMBER_1 || user?.role === RoleEnum.NUMBER_2) return true;
    if (data?.admins?.find((admin) => admin.id === user?.id)) return true;
    if (impliedAdminOfGroups?.find((g) => data?.id === g)) return true;
    return false;
  }, [data?.admins, user, impliedAdminOfGroups, data?.id]);

  const integrationConfig = configs?.find(
    (c) => c.type === FeatureConfigTypeEnum.integrations,
  ) as GroupHierarchyFeatureConfigModel;
  const hlConfig = integrationConfig?.config?.group_hierarchy;

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteGroup.mutateAsync({ siteId, groupId: data?.id || '' });
      setDeleteLoading(false);
      if (!pathname.includes('/admin/')) {
        navigate(`/${site.slug || ''}/home`);
      } else {
        navigate(-1);
      }
    } catch (e) {
      console.error(e);
      setDeleteLoading(false);
    }
  };

  useEffect(() => {
    if (tabSlug !== tab) {
      setTab(tabSlug || GroupAdminTabsEnum.INFO);
    }
  }, [tabSlug]);

  const handleGoBack = () => {
    if (pathname.includes('/admin/')) {
      navigate(`/${site?.slug || ''}/admin/community/groups`);
    } else {
      navigate(getContentRoute(site?.slug || '', groupSlug || '', 'group'));
    }
  };

  if (isLoading) {
    return (
      <Container>
        {headerSkeletons}
        {skeletons}
      </Container>
    );
  }

  if (!data) {
    return <>no data found</>;
  }

  if (!iAmAdmin) {
    return (
      <Container>
        <Typography variant='h6' textAlign={'center'} mt={4}>
          You do not have permission to view this page.
        </Typography>
      </Container>
    );
  }

  return (
    <Container>
      <Formik
        initialValues={data}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          setSubmitting(true);
          const newValues = { ...values };
          const slugChanged = newValues.slug !== groupSlug;
          const pathName = window.location.pathname;
          const routeType = pathName.includes('/admin/')
            ? ContentRouteTypeEnum.admin
            : ContentRouteTypeEnum.fet;
          try {
            const newData = await groupUpdate.mutateAsync({
              siteId,
              groupId: data?.id || '',
              data: newValues,
            });
            setSubmitting(false);
            resetForm({ values: newData });
            slugChanged &&
              navigate(getContentRoute(site.slug || '', newData.slug, 'group', routeType));
          } catch (e) {
            setSubmitting(false);
            console.error(e);
          }
        }}
      >
        {(formik) => {
          const { handleSubmit, isSubmitting, dirty, handleReset, errors, values } = formik;

          return (
            <>
              {data?.date_released && new Date(data.date_released) > new Date() && (
                <Alert severity='warning'>
                  This group's release date is in the future. It will not show up for end users on
                  the platform.
                </Alert>
              )}
              {data && !data?.date_released && (
                <Alert severity='warning'>
                  This group does not have a release date. It will not show up for end users on the
                  platform.
                </Alert>
              )}
              <Form onSubmit={handleSubmit}>
                <Box>
                  <AdminEditPanelHeader
                    shouldShow={dirty && Object.values(errors).length === 0}
                    title={data?.title || ''}
                    onClickDiscard={handleReset}
                    onClickSave={handleSubmit}
                    onClickGoBack={() => {
                      handleGoBack();
                    }}
                    isSubmitting={isSubmitting}
                    bannerImageUrl={data?.banner_image}
                  />
                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                      value={tab}
                      onChange={handleTabChange}
                      aria-label='basic tabs example'
                      sx={{ '.MuiTab-root': { textTransform: 'none' } }}
                    >
                      <Tab
                        label='Group Info'
                        component={Link}
                        to={GroupAdminTabsEnum.INFO}
                        {...a11yProps(GroupAdminTabsEnum.INFO)}
                        value={GroupAdminTabsEnum.INFO}
                      />
                      <Tab
                        label='Members'
                        component={Link}
                        to={GroupAdminTabsEnum.MEMBERS}
                        {...a11yProps(GroupAdminTabsEnum.MEMBERS)}
                        value={GroupAdminTabsEnum.MEMBERS}
                      />
                      <Tab
                        label='Resources'
                        component={Link}
                        to={GroupAdminTabsEnum.RESOURCES}
                        {...a11yProps(GroupAdminTabsEnum.RESOURCES)}
                        value={GroupAdminTabsEnum.RESOURCES}
                      />
                      <Tab
                        component={Link}
                        to={GroupAdminTabsEnum.SETTINGS}
                        label='Additional Settings'
                        {...a11yProps(GroupAdminTabsEnum.SETTINGS)}
                        value={GroupAdminTabsEnum.SETTINGS}
                      />
                      <Button
                        onClick={() => setDeleteDialogOpen(true)}
                        sx={{ marginLeft: 'auto', textTransform: 'none' }}
                        size='small'
                      >
                        <DeleteIcon sx={{ mr: 1, fontSize: 18 }} /> Delete Group
                      </Button>
                    </Tabs>
                  </Box>
                  <TabContext value={tab}>
                    <CustomTabPanel value={GroupAdminTabsEnum.INFO} index={0}>
                      <GroupInfo site={site} group={data} formik={formik} hlConfig={!!hlConfig} />
                    </CustomTabPanel>
                    <CustomTabPanel value={GroupAdminTabsEnum.MEMBERS} index={1}>
                      <GroupMembers site={site} group={data} />
                    </CustomTabPanel>
                    <CustomTabPanel value={GroupAdminTabsEnum.RESOURCES} index={2}>
                      <GroupFilesTab
                        siteId={siteId}
                        group={data}
                        refetchGroup={refetch}
                        isAdminPage={true}
                      />
                    </CustomTabPanel>
                    <CustomTabPanel value={GroupAdminTabsEnum.SETTINGS} index={3}>
                      <ForumFeedSettings group={data} formik={formik} site={site} />
                    </CustomTabPanel>
                  </TabContext>
                </Box>
              </Form>
            </>
          );
        }}
      </Formik>

      <JunoDialog
        id='delete_group_confirm'
        title='Delete Group?'
        subtitle='Are you sure you want to delete this group?'
        message='This action cannot be undone.'
        open={deleteDialogOpen}
        mode='delete'
        primaryBtnProps={{
          onClick: handleDelete,
          loading: deleteLoading,
          title: 'Delete',
        }}
        secondaryBtnProps={{
          onClick: () => setDeleteDialogOpen(false),
          title: 'Cancel',
        }}
      />
    </Container>
  );
};

export default CommunityGroupPanel;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: string;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <TabPanel value={value}>
      <Typography>{children}</Typography>
    </TabPanel>
  );
}
